import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leadsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, StageBadge, PriorityBadge, Button } from '../../components/ui';
import OverviewTab from './tabs/OverviewTab';
import ContactsTab from './tabs/ContactsTab';
import FollowUpsTab from './tabs/FollowUpsTab';
import TasksTab from './tabs/TasksTab';
import MeetingsTab from './tabs/MeetingsTab';
import DocumentsTab from './tabs/DocumentsTab';
import AudioTab from './tabs/AudioTab';
import AIStudioTab from './tabs/AIStudioTab';
import ActivityLogTab from './tabs/ActivityLogTab';
import BattleCardTab from './tabs/BattleCardTab';
import toast from 'react-hot-toast';

const TABS = [
  { id: 'overview',  label: 'Overview',      icon: '📋' },
  { id: 'contacts',  label: 'Contacts',       icon: '👥' },
  { id: 'followups', label: 'Follow-ups',     icon: '📅' },
  { id: 'tasks',     label: 'Tasks',          icon: '✅' },
  { id: 'meetings',  label: 'Meetings',       icon: '🎥' },
  { id: 'documents', label: 'Documents',      icon: '📎' },
  { id: 'audio',     label: 'Audio / Calls',  icon: '🎙' },
  { id: 'battlecard', label: 'Battle Card',    icon: '⚔️' },
  { id: 'ai',        label: 'AI Studio',      icon: '⚡' },
  { id: 'activity',  label: 'Activity',       icon: '📜' },
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canViewAllLeads, canAssignLeads } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [teamMembers, setTeamMembers] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchWithRetry = async (attemptsLeft) => {
      try {
        const res = await leadsAPI.getById(id);
        if (!cancelled) {
          setLead(res.data?.data);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 403) {
          setLoading(false);
          toast.error('You do not have permission to access this lead');
          navigate('/leads');
          return;
        }
        if (attemptsLeft > 0) {
          setTimeout(() => fetchWithRetry(attemptsLeft - 1), 1500);
        } else {
          setLoading(false); // set before navigate so component is in clean state
          toast.error('Lead not found');
          navigate('/leads');
        }
      }
    };
    fetchWithRetry(3);

    if (canAssignLeads) {
      usersAPI.getAll().then(r => {
        if (!cancelled) setTeamMembers(r.data?.data?.users || []);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [id, canAssignLeads]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete lead ${lead?.lead_id}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      navigate('/leads');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally { setDeleting(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={40} /></div>
  );

  if (!lead) return null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <button onClick={() => navigate('/leads')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, padding: 0 }}>← Leads</button>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <span style={{ fontSize: 13, color: '#1B2A6B', fontWeight: 700 }}>{lead.lead_id}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1B2A6B' }}>
              {lead.contact_name || lead.type_specific_data?.name || lead.type_specific_data?.institution_name || lead.customer_type}
            </h1>
            <StageBadge stage={lead.stage} />
            <PriorityBadge priority={lead.priority} />
            {lead.is_stale && (
              <span style={{ fontSize: 11, background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>⚠️ STALE</span>
            )}
            {lead.scholarship_flag && (
              <span style={{ fontSize: 11, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>🎓 Scholarship</span>
            )}
          </div>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {lead.customer_type}
            {lead.contact_phone && ` · ${lead.contact_phone}`}
            {lead.contact_email && ` · ${lead.contact_email}`}
            {lead.assigned_to && ` · Assigned to ${lead.assigned_to.name}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : '🗑 Delete'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e2e8f0', marginBottom: 24, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500,
            color: activeTab === tab.id ? '#1B2A6B' : '#64748b',
            borderBottom: `2px solid ${activeTab === tab.id ? '#C9A84C' : 'transparent'}`,
            marginBottom: -2, whiteSpace: 'nowrap', transition: 'all 0.15s', fontFamily: 'inherit'
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview'  && <OverviewTab lead={lead} onUpdate={setLead} teamMembers={teamMembers} />}
      {activeTab === 'contacts'  && <ContactsTab lead={lead} onLeadUpdate={setLead} />}
      {activeTab === 'followups' && <FollowUpsTab leadId={id} lead={lead} />}
      {activeTab === 'tasks'     && <TasksTab leadId={id} />}
      {activeTab === 'meetings'  && <MeetingsTab leadId={id} />}
      {activeTab === 'documents' && <DocumentsTab leadId={id} />}
      {activeTab === 'audio'     && <AudioTab leadId={id} />}
      {activeTab === 'battlecard' && <BattleCardTab leadId={id} />}
      {activeTab === 'ai'        && <AIStudioTab lead={lead} />}
      {activeTab === 'activity'  && <ActivityLogTab leadId={id} />}
    </div>
  );
}
